import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost, type PostValue } from './client'
import type { AdmView } from './admDashboard'

/**
 * ADM -> „Why discovery failed", calea noua. Perechile vechi, din
 * `application/controllers/Adm.php`:
 *
 *   errors()                    -> GET  /adm/errors
 *   error_detail($code)         -> GET  /adm/errors/{code}
 *   errorOccurrencesData($code) -> GET  /adm/errors/{code}/table
 *   setErrorState()             -> POST /adm/errors/state
 *
 * Calea NU poarta `/api/v2`: il pune `apiGet`/`apiPost`.
 *
 * O PALNIE, NU UN JURNAL. Categorie, apoi cod, apoi adresele din spate: o
 * singura subretea nerutata inseamna mii de caderi identice, si o lista plata
 * cu ele nu se poate nici citi, nici lucra.
 *
 * CATEGORIA ALEASA STA IN ADRESA (`?category=2`), nu in memoria ecranului: asa
 * legatura se poate da mai departe, si intoarcerea din fisa unui cod ajunge
 * inapoi la aceeasi felie. Palnia insa NU se filtreaza pe ea insasi - altfel ar
 * ramane un singur cartonas dupa primul clic.
 *
 * DREPTURILE le hotaraste serverul. `canWrite` spune doar daca se deseneaza
 * butoanele; refuzul insusi e pe server.
 */

/** O optiune de filtru, asa cum o trimite serverul. */
export interface Option {
  value: string
  label: string
}

/** Filtrele barei - firma NU se trimite, o ia serverul din jeton. */
export interface AdmFilterValues {
  days: number
  company_id: number
  relay_device_id: number
  run_type: number
  state: number
}

/** Un cartonas din palnie: cate caderi si cate coduri pe o categorie. */
export interface ErrorCategory {
  category: number
  title: string
  /** Iconita vine de la server, ca ea si eticheta sa nu se poata desparti. */
  icon: string
  codes: number
  value: number
}

/** Un rand de cod, cu propozitia care spune cine il poate repara. */
export interface ErrorCode {
  code: string
  category: number
  category_label: string
  rows_total: number
  occurrences: number
  companies: number
  first_seen: string
  last_seen: string
  guidance: string
}

export interface ErrorsScreen {
  pageTitle: string
  pageHeader: string
  breadcrumbs: string[]
  windows: number[]
  filters: AdmFilterValues
  /** Categoria aleasa, 0 = toate. */
  category: number
  views: AdmView[]
  categories: ErrorCategory[]
  codes: ErrorCode[]
  categoryLabels: Option[]
  states: Option[]
  canWrite: boolean
}

export interface ErrorDetailScreen {
  pageTitle: string
  pageHeader: string
  breadcrumbs: string[]
  windows: number[]
  filters: AdmFilterValues & { code: string }
  code: ErrorCode
  states: Option[]
  canWrite: boolean
}

/** O aparitie - o adresa pe care a cazut scanarea. */
export interface ErrorOccurrence {
  ID: number
  DT_RowId: string
  code: string
  category: number
  message: string
  occurrences: number
  first_seen: string
  last_seen: string
  state: number
  state_label: string
  run_id: number
  ci_id: number
  target_ip: string
  company_name: string
  relay_name: string
}

export interface ErrorOccurrencesPage {
  draw: number
  recordsTotal: number
  recordsFiltered: number
  data: ErrorOccurrence[]
}

/** Ce trimite ecranul cand cere lista de aparitii. */
export interface OccurrencesRequest {
  draw: number
  start: number
  length: number
  orderColumn: number
  orderDir: 'asc' | 'desc'
  search: string
  days: number
  /** '' inseamna „orice stare"; altfel una din `ErrorState`. */
  state: string
}

/** Starile - `Discovery_errors::STATE_*`. */
export const ERROR_STATE_ACTIVE = 1
export const ERROR_STATE_IGNORED = 2
export const ERROR_STATE_RESOLVED = 3

export function errorsScreenQuery(days: number, category: number) {
  const params = new URLSearchParams({ days: String(days) })
  if (category > 0) params.set('category', String(category))

  return queryOptions({
    queryKey: ['adm', 'errors', 'screen', days, category],
    queryFn: () => apiGet<ErrorsScreen>(`/adm/errors?${params.toString()}`),
  })
}

export function errorDetailQuery(code: string, days: number) {
  return queryOptions({
    queryKey: ['adm', 'errors', 'detail', code, days],
    queryFn: () => apiGet<ErrorDetailScreen>(`/adm/errors/${encodeURIComponent(code)}?days=${days}`),
  })
}

export function errorOccurrencesQuery(code: string, r: OccurrencesRequest) {
  const params = new URLSearchParams({
    draw: String(r.draw),
    start: String(r.start),
    length: String(r.length),
    orderColumn: String(r.orderColumn),
    orderDir: r.orderDir,
    search: r.search,
    days: String(r.days),
    state: r.state,
  })

  return queryOptions({
    queryKey: ['adm', 'errors', 'table', code, r],
    queryFn: () => apiGet<ErrorOccurrencesPage>(`/adm/errors/${encodeURIComponent(code)}/table?${params.toString()}`),
  })
}

/**
 * Schimba starea: ori a unui rand (`id`), ori a TOT ce poarta un cod.
 *
 * Sunt aceeasi hotarare la doua scari, si de-aia un singur capat: un releu care
 * a produs patru mii de caderi identice nu se poate rezolva rand cu rand.
 *
 * Filtrele ecranului pleaca in coada adresei, fiindca pe cod se scrie prin
 * ACELEASI conditii prin care se citeste - altfel s-ar putea ignora randuri pe
 * care ecranul nu le arata.
 */
export function setErrorState(target: { id?: number; code?: string }, state: number, days: number) {
  const body: Record<string, PostValue> = { state }
  if (target.id) body.id = target.id
  if (target.code) body.code = target.code

  return apiPost<{ status?: string; rows?: number; message: string }>(`/adm/errors/state?days=${days}`, body)
}
